import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart } from 'recharts';
import { Scale, Ruler, Clock, Percent, Dumbbell, Plus, Pencil, Trash2, TrendingUp, TrendingDown, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

// Chart styling constants (extracted to avoid re-creation on render)
const CHART_TICK = { fill: '#999999', fontSize: 11 };
const CHART_AXIS = { stroke: '#27272a' };
const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#111118',
  border: '1px solid #27272a',
  borderRadius: '2px',
  color: '#fff'
};

const CHART_DOMAIN_AUTO = ['auto', 'auto'];

export default function MetricsPage() {
  const [metrics, setMetrics] = useState([]);
  const [latestMetrics, setLatestMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedChart, setSelectedChart] = useState('weight');
  const [formData, setFormData] = useState({
    weight: '', age: '', height: '', metabolic_age: '',
    body_fat: '', muscle_mass: '', bone_mass: '', body_water: '',
    visceral_fat: '', bmr: '', physique_rating: '',
    timestamp: new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10)
  });

  // apiClient is a stable module-level singleton — not a state/prop dependency
  const fetchMetrics = useCallback(async () => {
    try {
      const [historyRes, latestRes] = await Promise.all([
        apiClient.get('/metrics?limit=100'),
        apiClient.get('/metrics/latest')
      ]);
      setMetrics(historyRes.data);
      setLatestMetrics(latestRes.data);
      
      // Pre-fill form with latest values
      if (latestRes.data && Object.keys(latestRes.data).length > 0) {
        setFormData({
          weight: latestRes.data.weight || '',
          age: latestRes.data.age || '',
          height: latestRes.data.height || '',
          metabolic_age: latestRes.data.metabolic_age || '',
          body_fat: latestRes.data.body_fat || '',
          muscle_mass: latestRes.data.muscle_mass || '',
          bone_mass: latestRes.data.bone_mass || '',
          body_water: latestRes.data.body_water || '',
          visceral_fat: latestRes.data.visceral_fat || '',
          bmr: latestRes.data.bmr || '',
          physique_rating: latestRes.data.physique_rating || ''
        });
      }
    } catch {
      toast.error('Erreur lors du chargement des métriques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.weight) {
      toast.error('Le poids est requis');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        weight: parseFloat(formData.weight),
        age: formData.age ? parseInt(formData.age) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        metabolic_age: formData.metabolic_age ? parseInt(formData.metabolic_age) : null,
        body_fat: formData.body_fat ? parseFloat(formData.body_fat) : null,
        muscle_mass: formData.muscle_mass ? parseFloat(formData.muscle_mass) : null,
        bone_mass: formData.bone_mass ? parseFloat(formData.bone_mass) : null,
        body_water: formData.body_water ? parseFloat(formData.body_water) : null,
        visceral_fat: formData.visceral_fat ? parseInt(formData.visceral_fat) : null,
        bmr: formData.bmr ? parseFloat(formData.bmr) : null,
        physique_rating: formData.physique_rating ? parseInt(formData.physique_rating) : null,
        timestamp: formData.timestamp ? formData.timestamp + 'T12:00:00Z' : null
      };

      if (editingMetric) {
        await apiClient.put(`/metrics/${editingMetric.id}`, payload);
        toast.success('Métrique mise à jour');
      } else {
        await apiClient.post('/metrics', payload);
        toast.success('Métrique enregistrée');
      }
      
      setShowForm(false);
      setEditingMetric(null);
      resetForm();
      fetchMetrics();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (metric) => {
    setEditingMetric(metric);
    setFormData({
      weight: metric.weight || '', age: metric.age || '', height: metric.height || '',
      metabolic_age: metric.metabolic_age || '', body_fat: metric.body_fat || '',
      muscle_mass: metric.muscle_mass || '', bone_mass: metric.bone_mass || '',
      body_water: metric.body_water || '', visceral_fat: metric.visceral_fat || '',
      bmr: metric.bmr || '', physique_rating: metric.physique_rating || '',
      timestamp: metric.timestamp ? metric.timestamp.slice(0, 10) : new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10)
    });
    setShowForm(true);
  };

  const handleDelete = async (metricId) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    
    try {
      await apiClient.delete(`/metrics/${metricId}`);
      toast.success('Entrée supprimée');
      fetchMetrics();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    if (latestMetrics) {
      setFormData({
        weight: latestMetrics.weight || '', age: latestMetrics.age || '',
        height: latestMetrics.height || '', metabolic_age: latestMetrics.metabolic_age || '',
        body_fat: latestMetrics.body_fat || '', muscle_mass: latestMetrics.muscle_mass || '',
        bone_mass: latestMetrics.bone_mass || '', body_water: latestMetrics.body_water || '',
        visceral_fat: latestMetrics.visceral_fat || '', bmr: latestMetrics.bmr || '',
        physique_rating: latestMetrics.physique_rating || '',
        timestamp: new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10)
      });
    }
  };

  // Calculate progression stats
  const getProgression = () => {
    if (metrics.length < 2) return null;
    
    const latest = metrics[0];
    const oldest = metrics[metrics.length - 1];
    const weekAgo = metrics.find(m => {
      const mDate = new Date(m.timestamp);
      const weekAgoDate = new Date();
      weekAgoDate.setDate(weekAgoDate.getDate() - 7);
      return mDate <= weekAgoDate;
    }) || metrics[Math.min(metrics.length - 1, 6)];

    return {
      weightChange: latest.weight && oldest.weight ? (latest.weight - oldest.weight).toFixed(1) : null,
      weightChangeWeek: latest.weight && weekAgo?.weight ? (latest.weight - weekAgo.weight).toFixed(1) : null,
      bodyFatChange: latest.body_fat && oldest.body_fat ? (latest.body_fat - oldest.body_fat).toFixed(1) : null,
      muscleChange: latest.muscle_mass && oldest.muscle_mass ? (latest.muscle_mass - oldest.muscle_mass).toFixed(1) : null,
      totalEntries: metrics.length,
      daysTracking: Math.ceil((new Date(latest.timestamp) - new Date(oldest.timestamp)) / (1000 * 60 * 60 * 24))
    };
  };

  const progression = getProgression();

  // Prepare chart data - reverse for chronological order
  const chartData = [...metrics].reverse().map(m => ({
    date: new Date(m.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    fullDate: new Date(m.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
    poids: m.weight,
    imc: m.bmi,
    graisse: m.body_fat,
    muscle: m.muscle_mass,
    tdee: m.tdee
  }));

  const chartOptions = [
    { key: 'weight', label: 'Poids', dataKey: 'poids', color: '#FF6B35', unit: 'kg' },
    { key: 'bmi', label: 'IMC', dataKey: 'imc', color: '#3b82f6', unit: '' },
    { key: 'bodyFat', label: 'Masse grasse', dataKey: 'graisse', color: '#ef4444', unit: '%' },
    { key: 'muscle', label: 'Masse musculaire', dataKey: 'muscle', color: '#10b981', unit: 'kg' },
    { key: 'tdee', label: 'TDEE', dataKey: 'tdee', color: '#f59e0b', unit: 'kcal' }
  ];

  const currentChartOption = chartOptions.find(o => o.key === selectedChart) || chartOptions[0];
  const chartDot = useMemo(() => ({ fill: currentChartOption.color, r: 3 }), [currentChartOption.color]);
  const chartActiveDot = useMemo(() => ({ r: 5, fill: currentChartOption.color }), [currentChartOption.color]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-safe">
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs text-[#FF6B35] uppercase tracking-widest">Métriques</p>
            <h1 className="text-xl font-bold text-white">Suivi corporel</h1>
          </div>
          <Button
            onClick={() => { setShowForm(true); setEditingMetric(null); resetForm(); }}
            data-testid="add-metrics-button"
            className="bg-[#FF6B35] text-white hover:bg-[#FF8C55]"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>
      </header>

      <main className="p-4 space-y-6 animate-fade-in">
        {/* Progression Summary */}
        {progression && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="progression-summary">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[#FF6B35]" />
              <h3 className="text-sm font-medium text-zinc-400">Progression</h3>
              <span className="text-xs text-zinc-500 ml-auto">{progression.totalEntries} entrées • {progression.daysTracking} jours</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {progression.weightChangeWeek !== null && (
                <div className="text-center">
                  <p className="text-xs text-zinc-500 mb-1">Cette semaine</p>
                  <div className={`flex items-center justify-center gap-1 ${parseFloat(progression.weightChangeWeek) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(progression.weightChangeWeek) <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                    <span className="font-mono font-bold">{progression.weightChangeWeek > 0 ? '+' : ''}{progression.weightChangeWeek}</span>
                    <span className="text-xs">kg</span>
                  </div>
                </div>
              )}
              {progression.bodyFatChange !== null && (
                <div className="text-center">
                  <p className="text-xs text-zinc-500 mb-1">Graisse</p>
                  <div className={`flex items-center justify-center gap-1 ${parseFloat(progression.bodyFatChange) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(progression.bodyFatChange) <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                    <span className="font-mono font-bold">{progression.bodyFatChange > 0 ? '+' : ''}{progression.bodyFatChange}</span>
                    <span className="text-xs">%</span>
                  </div>
                </div>
              )}
              {progression.muscleChange !== null && (
                <div className="text-center">
                  <p className="text-xs text-zinc-500 mb-1">Muscle</p>
                  <div className={`flex items-center justify-center gap-1 ${parseFloat(progression.muscleChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(progression.muscleChange) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span className="font-mono font-bold">{progression.muscleChange > 0 ? '+' : ''}{progression.muscleChange}</span>
                    <span className="text-xs">kg</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Current Stats Grid */}
        {latestMetrics && Object.keys(latestMetrics).length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <MetricCard icon={<Scale />} label="Poids" value={latestMetrics.weight} unit="kg" testId="current-weight" />
            <MetricCard icon={<Ruler />} label="IMC" value={latestMetrics.bmi} unit="" testId="current-bmi" />
            <MetricCard icon={<Clock />} label="Âge métab." value={latestMetrics.metabolic_age} unit="ans" testId="current-metabolic-age" />
            <MetricCard icon={<Percent />} label="Masse grasse" value={latestMetrics.body_fat} unit="%" testId="current-body-fat" />
            <MetricCard icon={<Dumbbell />} label="Masse musc." value={latestMetrics.muscle_mass} unit="kg" testId="current-muscle-mass" />
            <MetricCard icon={<Scale />} label="TDEE" value={latestMetrics.tdee} unit="kcal" testId="current-tdee" />
          </div>
        )}

        {/* Chart Section */}
        {chartData.length > 0 && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="metrics-chart">
            {/* Chart Type Selector */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-zinc-400">Évolution</h3>
              <div className="flex gap-1 flex-wrap justify-end">
                {chartOptions.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSelectedChart(opt.key)}
                    className={`px-2 py-1 text-xs rounded-sm transition-colors ${
                      selectedChart === opt.key 
                        ? 'bg-[#FF6B35] text-white font-bold' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id={`gradient-${currentChartOption.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={currentChartOption.color} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={currentChartOption.color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    tick={CHART_TICK}
                    axisLine={CHART_AXIS}
                    tickLine={false}
                  />
                  <YAxis 
                    domain={CHART_DOMAIN_AUTO}
                    tick={CHART_TICK}
                    axisLine={CHART_AXIS}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip 
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
                    formatter={(value) => [`${value} ${currentChartOption.unit}`, currentChartOption.label]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey={currentChartOption.dataKey} 
                    stroke={currentChartOption.color}
                    strokeWidth={2}
                    fill={`url(#gradient-${currentChartOption.key})`}
                    dot={chartDot}
                    activeDot={chartActiveDot}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* History Toggle */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-between p-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm hover:bg-[#1a1a24] transition-colors"
          data-testid="toggle-history"
        >
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-sm font-medium text-zinc-400">Historique complet</span>
            <span className="text-xs text-zinc-500">{metrics.length} entrées</span>
          </div>
          {showHistory ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>

        {/* History List */}
        {showHistory && metrics.length > 0 && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="metrics-history">
            <div className="max-h-96 overflow-y-auto divide-y divide-zinc-800">
              {metrics.map((m, i) => (
                <div key={m.id || i} className="p-4 hover:bg-[#1a1a24] transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-lg text-white">{m.weight} kg</span>
                        {m.bmi && <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">IMC {m.bmi}</span>}
                        {i === 0 && <span className="text-xs text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded">Dernier</span>}
                      </div>
                      <p className="text-xs text-zinc-500 mb-2">
                        {new Date(m.timestamp).toLocaleDateString('fr-FR', { 
                          weekday: 'long',
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
                        {m.body_fat && <span>Graisse: {m.body_fat}%</span>}
                        {m.muscle_mass && <span>Muscle: {m.muscle_mass} kg</span>}
                        {m.metabolic_age && <span>Âge métab: {m.metabolic_age}</span>}
                        {m.tdee && <span>TDEE: {m.tdee} kcal</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleEdit(m)}
                        className="p-2 text-zinc-500 hover:text-[#FF6B35] hover:bg-zinc-800 rounded-sm transition-colors"
                        data-testid={`edit-metric-${i}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(m.id)}
                        className="p-2 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-sm transition-colors"
                        data-testid={`delete-metric-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {metrics.length === 0 && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center" data-testid="empty-metrics">
            <Scale className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">Aucune mesure enregistrée</p>
            <p className="text-sm text-zinc-500">Ajoutez vos premières mesures pour commencer le suivi</p>
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingMetric(null); }}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingMetric ? 'Modifier la mesure' : 'Nouvelle mesure'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="metrics-form">
            {/* Date picker */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Date de la mesure
              </Label>
              <Input
                type="date"
                value={formData.timestamp}
                onChange={(e) => setFormData({ ...formData, timestamp: e.target.value })}
                data-testid="metrics-date-input"
                className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Poids (kg) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="73.5"
                  required
                  data-testid="weight-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Taille (cm)</Label>
                <Input
                  type="number"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="172"
                  data-testid="height-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Âge</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="32"
                  data-testid="age-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Âge métabolique</Label>
                <Input
                  type="number"
                  value={formData.metabolic_age}
                  onChange={(e) => setFormData({ ...formData, metabolic_age: e.target.value })}
                  placeholder="28"
                  data-testid="metabolic-age-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Masse grasse (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.body_fat}
                  onChange={(e) => setFormData({ ...formData, body_fat: e.target.value })}
                  placeholder="18.5"
                  data-testid="body-fat-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Masse musculaire (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.muscle_mass}
                  onChange={(e) => setFormData({ ...formData, muscle_mass: e.target.value })}
                  placeholder="35.2"
                  data-testid="muscle-mass-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Masse osseuse (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.bone_mass}
                  onChange={(e) => setFormData({ ...formData, bone_mass: e.target.value })}
                  placeholder="3.2"
                  data-testid="bone-mass-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Eau corporelle (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.body_water}
                  onChange={(e) => setFormData({ ...formData, body_water: e.target.value })}
                  placeholder="55.0"
                  data-testid="body-water-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Graisse viscerale</Label>
                <Input
                  type="number"
                  value={formData.visceral_fat}
                  onChange={(e) => setFormData({ ...formData, visceral_fat: e.target.value })}
                  placeholder="8"
                  data-testid="visceral-fat-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">BMR (kcal)</Label>
                <Input
                  type="number"
                  value={formData.bmr}
                  onChange={(e) => setFormData({ ...formData, bmr: e.target.value })}
                  placeholder="1650"
                  data-testid="bmr-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Type physique (1-9)</Label>
                <Input
                  type="number"
                  min="1"
                  max="9"
                  value={formData.physique_rating}
                  onChange={(e) => setFormData({ ...formData, physique_rating: e.target.value })}
                  placeholder="5"
                  data-testid="physique-rating-input"
                  className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono"
                />
              </div>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForm(false); setEditingMetric(null); }}
                className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-metrics-button"
                className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55]"
              >
                {saving ? 'Enregistrement...' : (editingMetric ? 'Mettre à jour' : 'Enregistrer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}

function MetricCard({ icon, label, value, unit, testId }) {
  return (
    <div className="metric-card p-4 rounded-sm" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#FF6B35] mb-2">
        <span className="w-4 h-4">{icon}</span>
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-mono text-2xl font-bold text-white">
          {value !== null && value !== undefined ? value : '—'}
        </span>
        {unit && <span className="text-sm text-zinc-500">{unit}</span>}
      </div>
    </div>
  );
}
